// api/missions.js - Mission Management API

import { apiClient } from './client';

class MissionAPI {
  constructor(client) {
    this.client = client;
  }

  // ========== BASIC CRUD ==========

  async getMissions(campaignId) {
    return this.client.request(`/api/missions?campaign_id=${campaignId}`);
  }

  async getMission(missionId) {
    return this.client.request(`/api/missions/${missionId}`);
  }

  async createMission(campaignId, missionData) {
    return this.client.request('/api/missions', {
      method: 'POST',
      body: JSON.stringify({
        campaign_id: campaignId,
        ...missionData,
      }),
    });
  }

  async updateMission(missionId, updates) {
    return this.client.request(`/api/missions/${missionId}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  }

  async deleteMission(missionId) {
    return this.client.request(`/api/missions/${missionId}`, {
      method: 'DELETE',
    });
  }

  // ========== MISSION LIFECYCLE ==========

  async startMission(missionId, squadMemberIds = []) {
    return this.updateMission(missionId, {
      status: 'in_progress',
      started_at: new Date().toISOString(),
      assigned_squad: JSON.stringify(squadMemberIds),
    });
  }

  async completeMission(missionId, results = {}) {
    return this.updateMission(missionId, {
      status: 'completed',
      completed_at: new Date().toISOString(),
      success: results.success !== undefined ? results.success : true,
      ...results,
    });
  }

  async failMission(missionId, reason = null) {
    return this.updateMission(missionId, {
      status: 'failed',
      completed_at: new Date().toISOString(),
      success: false,
      failure_reason: reason,
    });
  }

  async abortMission(missionId) {
    return this.updateMission(missionId, {
      status: 'aborted',
      completed_at: new Date().toISOString(),
    });
  }

  // ========== QUERIES ==========

  async getActiveMissions(campaignId) {
    const missions = await this.getMissions(campaignId);
    return missions.filter(m => m.status === 'active' || m.status === 'available');
  }

  async getInProgressMissions(campaignId) {
    const missions = await this.getMissions(campaignId);
    return missions.filter(m => m.status === 'in_progress');
  }

  async getCompletedMissions(campaignId) {
    const missions = await this.getMissions(campaignId);
    return missions.filter(m => m.status === 'completed');
  }

  async getMissionsByType(campaignId, missionType) {
    const missions = await this.getMissions(campaignId);
    return missions.filter(m => m.mission_type === missionType);
  }

  async getMissionsByDifficulty(campaignId, difficulty) {
    const missions = await this.getMissions(campaignId);
    return missions.filter(m => m.difficulty === difficulty);
  }

  // ========== STATISTICS ==========

  async getMissionStats(campaignId) {
    const missions = await this.getMissions(campaignId);
    
    return {
      total: missions.length,
      completed: missions.filter(m => m.status === 'completed').length,
      failed: missions.filter(m => m.status === 'failed').length,
      inProgress: missions.filter(m => m.status === 'in_progress').length,
      available: missions.filter(m => m.status === 'active' || m.status === 'available').length,
      successRate: missions.filter(m => m.success === true).length / missions.filter(m => m.status === 'completed').length || 0,
    };
  }
}

// Export singleton instance
export const missionAPI = new MissionAPI(apiClient);

// Also export class
export default MissionAPI;